import { useEffect, useState } from 'react'
import type { FC } from 'react'
import { useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Badge, Box, Divider, Drawer, Hidden } from '@material-ui/core'
import DashboardIcon from '@material-ui/icons/Dashboard'
import PeopleAltIcon from '@material-ui/icons/PeopleAlt'
import SettingsIcon from '@material-ui/icons/Settings'
import MmsIcon from '@material-ui/icons/Mms'
import SubjectIcon from '@material-ui/icons/Subject'
import VoicemailIcon from '@material-ui/icons/Voicemail'
import PhoneIcon from '@material-ui/icons/Phone'
import { useSelector } from 'react-redux'
import { RootState } from 'src/store'
import NavSection from '../NavSection'
import Scrollbar from '../Scrollbar'

interface DashboardSidebarProps {
  onMobileClose: () => void
  openMobile: boolean
  phoneEnabled: boolean
}

const phoneOnlySections = ['Voicemail', 'Calls']

const items = [
  {
    title: '',
    items: [
      {
        title: 'Dashboard',
        path: '/dashboard/overview',
        icon: <DashboardIcon fontSize="small" />
      },
      {
        title: 'Voicemail',
        path: '/dashboard/voicemail',
        icon: <VoicemailIcon fontSize="small" />,
        info: null
      },
      {
        title: 'Communications',
        path: '/dashboard/communications',
        icon: <SubjectIcon fontSize="small" />
      },
      {
        title: 'Messages',
        path: '/dashboard/sms',
        icon: <MmsIcon fontSize="small" />
      },
      {
        title: 'Calls',
        path: '/dashboard/calls',
        icon: <PhoneIcon fontSize="small" />
      },
      {
        title: 'Contacts',
        path: '/dashboard/contacts',
        icon: <PeopleAltIcon fontSize="small" />
      },
      {
        title: 'Settings',
        path: '/dashboard/settings',
        icon: <SettingsIcon fontSize="small" />
      }
    ]
  }
]

const DashboardSidebar: FC<DashboardSidebarProps> = (props) => {
  const { unread } = useSelector((state: RootState) => state.voicemails)
  const [navItems, setNavItems] = useState(items)
  const { onMobileClose, openMobile, phoneEnabled } = props
  const location = useLocation()
  if (!phoneEnabled) {
    navItems[0].items = navItems[0].items.filter(
      (section) => !phoneOnlySections.includes(section.title)
    )
  }

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose()
    }
  }, [location.pathname])

  useEffect(() => {
    if (unread) {
      navItems[0].items.forEach((item) => {
        if (item.title === 'Voicemail') {
          item.info = (
            <Badge
              badgeContent={`${unread}`}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            />
          )
        }
      })
      const updatedNav = [...navItems]
      setNavItems(updatedNav)
    } else {
      navItems[0].items.forEach((item) => {
        if (item.title === 'Voicemail') {
          item.info = null
        }
      })
    }
  }, [unread])

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      <Scrollbar options={{ suppressScrollX: true }}>
        <Box sx={{ p: 2 }}>
          {navItems.map((section) => (
            <NavSection
              key={section.title}
              pathname={location.pathname}
              sx={{
                '& + &': {
                  mt: 3
                }
              }}
              {...section}
            />
          ))}
        </Box>
        <Divider />
      </Scrollbar>
    </Box>
  )

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          PaperProps={{
            sx: {
              backgroundColor: 'background.paper',
              width: 280
            }
          }}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <Drawer
          anchor="left"
          open
          PaperProps={{
            sx: {
              backgroundColor: 'background.paper',
              height: 'calc(100% - 64px) !important',
              top: '64px !Important',
              width: 190
            }
          }}
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  )
}

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  phoneEnabled: PropTypes.bool
}

export default DashboardSidebar
